import { AppBar, Box, Toolbar, Tooltip, IconButton, ButtonBase } from '@mui/material';
import { useSelector } from 'react-redux';
import { AppState } from '../store';
import { HeaderLogin } from '.';
import HelpIcon from '@mui/icons-material/Help';
import { useTranslation } from 'react-i18next';
import WarningIcon from '@mui/icons-material/Warning';
import LockIcon from '@mui/icons-material/Lock';
import EditOffIcon from '@mui/icons-material/EditOff';
import { EConfigurationType, EPageNames } from '../data/Constants';
import { GetToken, IsAppOpenedInIframe, navigateToHome, showHeaderFooter } from '../services';
import { useNavigate } from 'react-router';
import { IsDefaultFlowInSessionStore } from '../services/SessionStoreHelperFunctions';
import { JSX } from 'react';

interface IMiddleContentProps {
  icon: JSX.Element;
  titleKey: string;
  messageKey: string;
  className: string;
}

const getMiddleContent = (
  state: string | undefined,
  access: string | undefined
): IMiddleContentProps | null => {
  const configState = state ?? access;

  switch (configState) {
    case EConfigurationType.Temp:
      return {
        icon: <WarningIcon className="icon" />,
        titleKey: 'state.temp.label',
        messageKey: 'state.temp.message',
        className: 'default',
      };
    case EConfigurationType.UnAvailable:
      return {
        icon: <WarningIcon className="icon" />,
        titleKey: 'state.unavailable.label',
        messageKey: 'state.unavailable.message',
        className: 'default',
      };
    case EConfigurationType.Frozen:
      return {
        icon: <LockIcon className="icon" />,
        titleKey: 'state.frozen.label',
        messageKey: 'state.frozen.message',
        className: 'frozen',
      };
    case EConfigurationType.ReadOnly:
      return {
        icon: <EditOffIcon className="icon" />,
        titleKey: 'state.readOnly.label',
        messageKey: 'state.readOnly.message',
        className: 'readOnly',
      };
    default:
      return null;
  }
};

/**
 * Renders the header which also includes the buttons to hide/show the TabDrawer and InfoPanel
 * @returns {JSX.Element} the header component
 */
export const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { token } = GetToken();

  const configuration = useSelector((state: AppState) => state.configuration);

  if (!configuration || IsAppOpenedInIframe() || showHeaderFooter()) {
    return null;
  }

  const philipsLogo = '../../public/png/logo.png';
  const middleContent = token
    ? getMiddleContent(configuration.savedConfiguration?.state, configuration.access)
    : null;

  return (
    <AppBar className="headerStyle root" position="static">
      <Toolbar className="toolbar">
        <Box className="toolbarSection">
          {IsDefaultFlowInSessionStore() && location.pathname !== EPageNames.LandingPage ? (
            <ButtonBase
              onClick={() => navigateToHome(navigate)}
              className="headerLogoButton"
              aria-label="Go to Home"
            >
              <img className="zoom80 headerLogo" src={philipsLogo} alt="Philips Logo" />
            </ButtonBase>
          ) : (
            <img className="zoom80 " src={philipsLogo} alt="philips-logo" />
          )}
        </Box>
        {middleContent && (
          <div className={`rowFlex headerBanner state ${middleContent.className}`}>
            <span className="colFlex stateIcon">{middleContent.icon}</span>
            &nbsp;
            <div className="message">
              <h2 className="m-0 text-center">{t(middleContent.titleKey)}</h2>
              <span>{t(middleContent.messageKey)}</span>
            </div>
          </div>
        )}
        <Box className="toolbarSection">
          <Tooltip className="tooltip" title={t('tooltip.help')}>
            <IconButton className="button-icon">
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <HeaderLogin />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
